import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import Header from '../../components/ui/Header';

import UserManagement from './components/UserManagement';
import ComingSoonManagement from './components/ComingSoonManagement';
import FeaturedContentManagement from './components/FeaturedContentManagement';
import SystemOverview from './components/SystemOverview';
import PasswordChangeUtility from './components/PasswordChangeUtility';
import LogoBrandingManagement from './components/LogoBrandingManagement';
import { userService } from '../../services/userService';

const AdminPortal = () => {
  const { user, userProfile, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    adminUsers: 0,
    premiumUsers: 0
  });

  useEffect(() => {
    // Load initial stats when component mounts
    if (userProfile?.role === 'admin') {
      loadSystemStats();
    }
  }, [userProfile]);

  const loadSystemStats = async () => {
    try {
      const result = await userService?.getUserStats();
      setStats(result);
    } catch (error) {
      console.error('Error loading system stats:', error);
      // Keep fallback empty state
      setStats({
        totalUsers: 0,
        activeUsers: 0,
        adminUsers: 0,
        premiumUsers: 0
      });
    }
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-20 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-muted rounded w-64 mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                {[...Array(4)]?.map((_, i) => (
                  <div key={i} className="h-32 bg-muted rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Redirect non-admin users
  if (!user || userProfile?.role !== 'admin') {
    return <Navigate to="/homepage" replace />;
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: 'BarChart3', description: 'System statistics and health' },
    { id: 'users', name: 'User Management', icon: 'Users', description: 'Manage all user accounts' },
    { id: 'coming-soon', name: 'Coming Soon', icon: 'Clock', description: 'Site access controls' },
    { id: 'featured', name: 'Featured Content', icon: 'Image', description: 'Homepage photo management' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mt-8">
          {/* Navigation Tabs */}
          <div className="border-b border-border">
            <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
              {[
                { id: 'overview', name: 'System Overview', icon: '📊' },
                { id: 'users', name: 'User Management', icon: '👥' },
                { id: 'featured', name: 'Featured Content', icon: '⭐' },
                { id: 'coming-soon', name: 'Coming Soon', icon: '🚀' },
                { id: 'branding', name: 'Logo & Branding', icon: '🎨' },
                { id: 'password-utility', name: 'Password Utility', icon: '🔐' }
              ]?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                >
                  <span>{tab?.icon}</span>
                  {tab?.name}
                </button>
              ))}
            </nav>
          </div>

          {activeTab === 'overview' && <SystemOverview stats={stats} onRefresh={loadSystemStats} />}
          {activeTab === 'users' && <UserManagement onStatsChange={loadSystemStats} />}
          {activeTab === 'featured' && <FeaturedContentManagement />}
          {activeTab === 'coming-soon' && <ComingSoonManagement />}
          {activeTab === 'branding' && <LogoBrandingManagement />}
          {activeTab === 'password-utility' && <PasswordChangeUtility />}
        </div>
      </div>
    </div>
  );
};

export default AdminPortal;